import type { paths } from '@cou/contracts';
import { notifyUnauthorized } from '../session/unauthorized-signal';

/*
 * The Live-catalogue browse client (API-16), used to enrich a Contest's curated markets with
 * their live Line/verdict/quote — the readiness checklist and Slate tab's only source for this,
 * since AdminContest.curation carries only pointers (eventIds/marketIds), not values.
 */

type CatalogueMarket =
  paths['/api/admin/catalogue/events/{id}/markets']['get']['responses'][200]['content']['application/json']['markets'][number];

type CatalogueEvent =
  paths['/api/admin/catalogue/events']['get']['responses'][200]['content']['application/json']['events'][number];

type ContestSlateResponse =
  paths['/api/admin/contests/{id}/slate']['get']['responses'][200]['content']['application/json'];

/** How a Market landed, once settlement has graded it (CAE-558). */
export interface MarketSettlement {
  /** The resulted value the frozen Line was graded against; null on a scoreless Void. */
  readonly finalValue: number | null;
  /** The side that won — or `void`, which is never a losing side (CONTEXT → Void). */
  readonly winner: 'over' | 'under' | 'void';
}

export interface CuratedMarket {
  readonly id: string;
  readonly eventId: string;
  readonly name: string;
  readonly marketType: string;
  readonly line: number;
  /**
   * Present only on a graded Market of the frozen snapshot; the Live-catalogue path has no
   * results to report, and a finalised-but-unsettled Market has none yet.
   */
  readonly settlement?: MarketSettlement;
  /**
   * Live selectability/quote, present only on the pre-finalise Live-catalogue read. A finalised
   * Contest's Slate is served frozen from the snapshot (CAE-485), which carries neither — a
   * pruned live quote must never blank a frozen row, so these are optional and simply omitted.
   */
  readonly verdict?: CatalogueMarket['verdict'];
  readonly quote?: CatalogueMarket['quote'];
}

export interface CuratedEvent {
  readonly id: string;
  readonly name: string;
  readonly startTime: string;
  readonly competition: string | null;
}

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

function authHeaders(adminToken: string): HeadersInit {
  return { authorization: `Bearer ${adminToken}` };
}

/**
 * Fetches each curated event's Markets (API-16 has no batch-by-ids route) and filters down to
 * the curated `marketIds`. Bounded by the curated event count (typically a handful per Contest).
 */
export async function getCuratedMarkets(
  adminToken: string,
  eventIds: readonly string[],
  marketIds: readonly string[],
): Promise<readonly CuratedMarket[]> {
  if (marketIds.length === 0) return [];
  const wanted = new Set(marketIds);
  // allSettled, not all: one event's fetch throwing (network error) shouldn't discard markets
  // already fetched successfully from every other curated event.
  const perEvent = await Promise.allSettled(
    eventIds.map(async (eventId) => {
      const response = await fetch(`${apiBase()}/api/admin/catalogue/events/${eventId}/markets`, {
        method: 'GET',
        headers: authHeaders(adminToken),
      });
      if (!response.ok) {
        notifyUnauthorized(response.status);
        return [];
      }
      const body = (await response.json()) as { markets: CatalogueMarket[] };
      return body.markets
        .filter((market) => wanted.has(market.id))
        .map((market) => ({
          id: market.id,
          eventId,
          name: market.name,
          marketType: market.marketType,
          line: market.line,
          verdict: market.verdict,
          quote: market.quote,
        }));
    }),
  );
  return perEvent.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
}

/**
 * Resolves curated Event ids to display names/start times for the Overview slate
 * preview and Slate tab event heads. API-16 has no batch-by-ids route, so this
 * browses the allowlisted list once and filters client-side.
 */
export async function getCuratedEvents(
  adminToken: string,
  eventIds: readonly string[],
): Promise<readonly CuratedEvent[]> {
  if (eventIds.length === 0) return [];
  const response = await fetch(`${apiBase()}/api/admin/catalogue/events`, {
    method: 'GET',
    headers: authHeaders(adminToken),
  });
  if (!response.ok) {
    notifyUnauthorized(response.status);
    return [];
  }
  const body = (await response.json()) as { events: CatalogueEvent[] };
  const byId = new Map(body.events.map((event) => [event.id, event]));
  // Preserve the curation order rather than the catalogue's browse order.
  return eventIds.flatMap((id) => {
    const event = byId.get(id);
    return event
      ? [
          {
            id: event.id,
            name: event.name,
            startTime: event.startTime,
            competition: event.competition,
          },
        ]
      : [{ id, name: id, startTime: '', competition: null }];
  });
}

export interface ContestSlate {
  readonly events: readonly CuratedEvent[];
  readonly markets: readonly CuratedMarket[];
}

/**
 * A finalised Contest's frozen Slate (CAE-485): Events/Markets read from the immutable
 * `contest_*` snapshot, independent of the Live catalogue. Shaped as {@link CuratedEvent} /
 * {@link CuratedMarket} so the Slate/Overview render the same way as the live path — only the
 * live-only `verdict`/`quote` are absent, the slate being frozen.
 */
export async function getContestSlate(
  adminToken: string,
  contestId: string,
): Promise<ContestSlate> {
  const response = await fetch(`${apiBase()}/api/admin/contests/${contestId}/slate`, {
    method: 'GET',
    headers: authHeaders(adminToken),
  });
  if (!response.ok) {
    notifyUnauthorized(response.status);
    return { events: [], markets: [] };
  }
  const body = (await response.json()) as ContestSlateResponse;
  return {
    events: body.events.map((event) => ({
      id: event.id,
      name: event.name,
      startTime: event.startTime,
      competition: event.competition,
    })),
    markets: body.markets.map((market) => ({
      id: market.id,
      eventId: market.eventId,
      name: market.name,
      marketType: market.marketType,
      line: market.line,
      settlement: readSettlement(market.outcomes),
    })),
  };
}

type SlateOutcome = ContestSlateResponse['markets'][number]['outcomes'][number];

/**
 * Reduce a frozen Market's two graded Outcome rows to how the Market landed. Settlement writes
 * the same resulted score to both sides and the derived verdict to each (ADR-0013), so the
 * winning side is simply the one holding the WIN.
 */
function readSettlement(outcomes: readonly SlateOutcome[]): MarketSettlement | undefined {
  // A VOID is checked first and reported as its own outcome: the market failed, not a side, so
  // it must never be rendered as one side losing (CONTEXT → Void).
  const voided = outcomes.find((outcome) => outcome.result === 'VOID');
  if (voided) return { finalValue: voided.finalValue, winner: 'void' };
  const winner = outcomes.find((outcome) => outcome.result === 'WIN');
  if (!winner) return undefined;
  return { finalValue: winner.finalValue, winner: winner.direction };
}

export interface CatalogueClient {
  getCuratedMarkets(
    adminToken: string,
    eventIds: readonly string[],
    marketIds: readonly string[],
  ): Promise<readonly CuratedMarket[]>;
  getCuratedEvents(
    adminToken: string,
    eventIds: readonly string[],
  ): Promise<readonly CuratedEvent[]>;
  getContestSlate(adminToken: string, contestId: string): Promise<ContestSlate>;
}

export const defaultCatalogueClient: CatalogueClient = {
  getCuratedMarkets,
  getCuratedEvents,
  getContestSlate,
};
